<template>
  <div style="border: 1px solid #ccc; margin-bottom: 10px">
    <Toolbar style="border-bottom: 1px solid #ccc" :editor="editorRef" :defaultConfig="toolbarConfig" :mode="mode" />
    <Editor style="height: 400px; overflow-y: hidden; resize: vertical" v-model="editorValue"
      :defaultConfig="editorConfig" :mode="isGridReport ? 'simple' : 'default'" @onCreated="handleCreated"
      @onChange="handleChange" />
  </div>
</template>

<script>
import "@wangeditor/editor/dist/css/style.css"; // 引入 css

import { onBeforeUnmount, ref, shallowRef, onMounted, watch } from "vue";
import { SlateNode, IDomEditor, Editor, Toolbar } from "@wangeditor/editor-for-vue";
import { emit } from "process";
import { useQuasar } from "quasar";
import { request, upload } from "@/request/api";
import { useI18n } from "vue-i18n";

// 专门为 Grid++Report 清洗和转换 HTML 的工具函数
function cleanHtmlForGridReport(html) {
  if (!html) return "";

  // 1. wangEditor 的 <strong> 转为 Grid++ 要求的 <b>
  html = html.replace(/<strong>/g, "<b>").replace(/<\/strong>/g, "</b>");

  // 2. wangEditor 的 <em> 转为 Grid++ 要求的 <i>
  html = html.replace(/<em>/g, "<i>").replace(/<\/em>/g, "</i>");

  // 3. wangEditor 的 <s> 转为 Grid++ 要求的 <strike>
  html = html.replace(/<s>/g, "<strike>").replace(/<\/s>/g, "</strike>");

  // 4. 将 <span style="color: xxx;"> 完美转换为 <font color="xxx">
  html = html.replace(/<span style="color:\s*([^;"]+);?">([\s\S]*?)<\/span>/gi, (match, colorVal, content) => {
    // 如果是 rgb(r, g, b) 格式，自动转换为 Grid++ 绝对支持的 16 进制 #RRGGBB 格式
    if (colorVal.includes("rgb")) {
      const rgbMatches = colorVal.match(/\d+/g);
      if (rgbMatches && rgbMatches.length >= 3) {
        const r = parseInt(rgbMatches[0]).toString(16).padStart(2, "0");
        const g = parseInt(rgbMatches[1]).toString(16).padStart(2, "0");
        const b = parseInt(rgbMatches[2]).toString(16).padStart(2, "0");
        colorVal = `#${r}${g}${b}`.toUpperCase();
      }
    }
    return `<font color="${colorVal}">${content}</font>`;
  });

  return html;
}

export default {
  name: "WEditor",
  props: {
    editorValue: { default: "" },
    isReadOnly: { default: false },
    // 是否为 Grid++ 报表模式，默认 false 不联动
    isGridReport: { type: Boolean, default: false }
  },
  components: { Editor, Toolbar },
  setup(props, context) {
    const $q = useQuasar();
    const { locale, t } = useI18n();

    // 编辑器实例，必须用 shallowRef
    const editorRef = shallowRef();

    let max_img_size = $q.localStorage.getItem("sys_param")["max_img_size"] ? $q.localStorage.getItem("sys_param")["max_img_size"] : 2;
    let max_video_size = $q.localStorage.getItem("sys_param")["max_video_size"] ? $q.localStorage.getItem("sys_param")["max_video_size"] : 20;

    // 内容 HTML
    // const valueHtml = ref("");

    // 模拟 ajax 异步获取 content
    onMounted(() => {
      // setTimeout(() => {
      //   valueHtml.value = "<p>模拟 Ajax 异步设置内容</p>";
      // }, 1500);
    });

    const toolbarConfig = {};
    const editorConfig = { MENU_CONF: {} }; // 移到这里统一初始化，方便后续修改

    // 根据模式动态配置工具栏和气泡菜单
    if (props.isGridReport) {
      // 报表模式：只留下 Grid++ 能解析的菜单
      toolbarConfig.toolbarKeys = [
        "bold",         // 粗体
        "italic",       // 斜体
        "underline",    // 下划线
        "through",      // 删除线
        "color",        // 文字颜色
        "clearStyle",   // 清除格式
        "|",
        "undo",         // 撤销
        "redo"          // 重做
      ];
    } else {
      // 原有逻辑：常规全功能模式
      toolbarConfig.excludeKeys = ["fullScreen"];
    }

    editorConfig.MENU_CONF["uploadImage"] = {
      // 20260612 修改为自定义上传，不使用base64文件保存到数据库中
      maxFileSize: max_img_size * 1024 * 1024,
      allowedFileTypes: ['image/*'],

      async customUpload(file, insertFn) {
        const formData = new FormData();
        formData.append('file', file);

        if (file.size > (max_img_size * 1024 * 1024)) {
          $q.notify({
            type: "warning",
            message: t("image_size_cannot_be_larger_than") + max_img_size + " Mb",
            position: "center",
          });
          return;
        }

        try {
          upload("upload_image", formData, "", "multipart/form-data")
            .then((res) => {
              const imgUrl = res["file_name"];
              insertFn(imgUrl, file.name, imgUrl);
            })
            .catch((error) => {
              console.log(error);
            });
        } catch (error) {
          console.error('图片上传失败', error);
        }
      }
    };

    editorConfig.MENU_CONF["uploadVideo"] = {
      maxFileSize: max_video_size * 1024 * 1024,
      allowedFileTypes: ['video/*'],

      async customUpload(file, insertFn) {
        const formData = new FormData();
        formData.append('file', file);

        if (file.size > (max_video_size * 1024 * 1024)) {
          $q.notify({
            type: "warning",
            message: t("video_size_cannot_be_larger_than") + max_video_size + " Mb",
            position: "center",
          });
          return;
        }

        try {
          upload("upload_file", formData, "", "multipart/form-data")
            .then((res) => {
              const fileUrl = res["file_name"];
              insertFn(fileUrl, file.name, fileUrl);
            })
            .catch((error) => {
              console.log(error);
            });
        } catch (error) {
          console.error('视频上传失败', error);
        }
      }
    };

    editorConfig.readOnly = props.isReadOnly;

    watch(
      () => props.isReadOnly,
      () => {
        editorConfig.readOnly = props.isReadOnly;
        if (editorConfig.readOnly) {
          editorRef.value.disable();
        } else {
          editorRef.value.enable();
        }
      }
    );

    onBeforeUnmount(() => {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.destroy();
    });

    const handleCreated = (editor) => {
      editorRef.value = editor;
    };

    function handleChange(editor) {
      let rawHtml = editor.getHtml();

      // 如果开启了报表模式，进行 HTML 清洗和转换
      if (props.isGridReport) {
        rawHtml = cleanHtmlForGridReport(rawHtml);
      }

      context.emit("onChange", rawHtml);
    }

    return {
      editorRef,
      mode: "default",
      toolbarConfig,
      editorConfig,
      handleCreated,
      handleChange,
    };
  },
};
</script>